import * as XLSX from 'xlsx-js-style';

export interface ExportColumn {
  label: string;
  value: string | number;
  align?: 'left' | 'right' | 'center';
}

export interface ExportSummary {
  totalDuration: string;
  billableDuration: string;
  amount: string;
}

export interface ExportData {
  filename: string;
  columns: ExportColumn[];
  rows: ExportColumn[][];
  summary?: ExportSummary;
  dateRange?: string;
  tableOptions?: Record<string, any>;
}

export function exportToCSV(data: ExportData): void {
  const escapeCell = (value: string | number): string => {
    const str = String(value);
    if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return `"${str}"`;
  };

  const headers = data.columns.map((col) => escapeCell(col.label));
  const csvData = data.rows.map((row) => row.map((cell) => escapeCell(cell.value)));

  let csvContent: string[];
  if (data.summary) {
    const summaryRow = [
      escapeCell(`Total Duration: ${data.summary.totalDuration}`),
      escapeCell(`Billable Duration: ${data.summary.billableDuration}`),
      escapeCell(`Amount: ${data.summary.amount}`),
      '""',
    ];
    csvContent = [summaryRow, headers, ...csvData].map((row) => row.join(','));
  } else {
    csvContent = [headers, ...csvData].map((row) => row.join(','));
  }

  const bom = '\uFEFF';
  const finalContent = bom + csvContent.join('\n');
  const blob = new Blob([finalContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${data.filename}.csv`;
  link.click();
}

export function exportToExcel(data: ExportData): void {
  let worksheetData: any[][];
  if (data.summary) {
    const summaryRow = [
      `Total Duration: ${data.summary.totalDuration}`,
      `Billable Duration: ${data.summary.billableDuration}`,
      `Amount: ${data.summary.amount}`,
      '',
    ];
    worksheetData = [
      summaryRow,
      data.columns.map((col) => col.label),
      ...data.rows.map((row) => row.map((cell) => cell.value)),
    ];
  } else {
    worksheetData = [
      data.columns.map((col) => col.label),
      ...data.rows.map((row) => row.map((cell) => cell.value)),
    ];
  }
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Apply left alignment to all cells
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cellAddress]) continue;
      if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
      worksheet[cellAddress].s.alignment = { horizontal: 'left' };
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, `${data.filename}.xlsx`);
}

